import { http } from '../lib/http'
import type { Facets, Vehicle } from './vehicles'

/* The only module that talks to the API. Everything above it — pages,
   sections, the React Query hooks in hooks/useVehicleQueries — asks through
   these functions, so a change of route or of query-parameter name is felt
   here and nowhere else.

   The filtering and sorting that used to happen in this file now happens on
   the server: the query goes up, the rows the page renders come back. */

/* The whole filter panel, as the API takes it. The checkbox groups travel
   comma-separated — ?brand=Toyota,Honda — because that is what a list of
   ticked boxes is, and because it is what the URL the visitor can share
   already holds. Anything left undefined is not sent at all. */
export type VehicleQuery = {
  brand?: string
  model?: string
  year?: string | number
  colour?: string
  minPrice?: number
  maxPrice?: number
  /** 'lease' for cars a bank will finance, 'cash' for the ones it will not. */
  payment?: 'cash' | 'lease' | ''
  /** 'insurance,registration' — every one of them carried, not any. */
  includes?: string
  /** Which side of the floor's monthly band to keep. */
  instalment?: 'under' | 'over' | ''
  /** Only cars standing on the floor today: a reserved one is in the showroom
      but is not one anybody else can have. */
  available?: boolean
  /** The floor as a visitor browses it — everything but the sold cars. */
  unsold?: boolean
  financingAvailable?: boolean
  search?: string
}

export type VehicleSort = 'featured' | 'newest' | 'price-asc' | 'price-desc'

/* ---------- catalogue ---------- */

export async function getVehicles(
  query: VehicleQuery = {},
  sort: VehicleSort = 'featured',
): Promise<Vehicle[]> {
  const { data } = await http.get<Vehicle[]>('/vehicles', {
    params: { ...query, sort },
  })
  return data
}

export async function getVehicleById(id: string): Promise<Vehicle | null> {
  try {
    const { data } = await http.get<Vehicle>(`/vehicles/${id}`)
    return data
  } catch (error) {
    /* A car that is not there is an answer, not a failure — the detail page
       renders its 404 from it. Anything else is a real error and is thrown. */
    if (isNotFound(error)) return null
    throw error
  }
}

export async function getFeatured(limit = 6): Promise<Vehicle[]> {
  const { data } = await http.get<Vehicle[]>('/vehicles/featured', {
    params: { limit },
  })
  return data
}

export async function getNewArrivals(limit = 8): Promise<Vehicle[]> {
  const { data } = await http.get<Vehicle[]>('/vehicles/new-arrivals', {
    params: { limit },
  })
  return data
}

/** Cars in the same body class, for the "similar vehicles" rail. */
export async function getSimilar(id: string, limit = 4): Promise<Vehicle[]> {
  const { data } = await http.get<Vehicle[]>(`/vehicles/${id}/similar`, {
    params: { limit },
  })
  return data
}

/** The options the filter panel offers, with their counts.

    The chosen brands go up because they narrow the model list, and only the
    model list: every other count is over the whole floor, so a number beside
    a box never falls to zero as the box above it is ticked. */
export async function getFacets(brands: string[] = []): Promise<Facets> {
  const { data } = await http.get<Facets>('/vehicles/facets', {
    params: brands.length ? { brand: brands.join(',') } : undefined,
  })
  return data
}

/* ---------- companies ---------- */

/** A make the showroom carries, as the console filed it. The badge is an
    upload rather than artwork we ship: the console sets it once when the
    company is created, and every surface that shows a marque reads it from
    here. */
export type CompanyRow = {
  id: string
  name: string
  /** '/uploads/…png' — the badge, transparent. Empty when none was set. */
  logo: string
  active: boolean
  /** The model lines filed under this make. */
  models: string[]
  /** Cars carrying it, sold ones included — which is why the storefront
      counts its own floor rather than printing this. */
  vehicles: number
}

/** The makes worth offering: a retired one stays on the cars that already
    carry it but is not somewhere a visitor should be sent. */
export async function getCompanies(): Promise<CompanyRow[]> {
  const { data } = await http.get<{ rows: CompanyRow[] }>('/companies', {
    params: { active: true },
  })
  return data.rows
}

/* ---------- cities ---------- */

/** A city the showroom recognises, as the booking form's picker lists it. */
export type City = {
  id: string
  name: string
  /** Province or territory — 'Punjab', 'Azad Kashmir'. */
  region: string
  order: number
  /** Alternative spellings and abbreviations — 'pindi', 'swat', 'ajk'.
      Matched by the picker's search, never shown in it. */
  keywords: string
}

/* The whole list, once. Thirty-odd rows are smaller than the request that
   would narrow them, so the picker filters what it already has rather than
   asking the API on every keystroke. The endpoint takes a search term too,
   for the day the list is long enough to need it. */
export async function getCities(search?: string): Promise<City[]> {
  const { data } = await http.get<{ rows: City[]; total: number }>('/cities', {
    params: search ? { search } : undefined,
  })
  return data.rows
}

/* ---------- video reviews ---------- */

/** A customer video review as the console filed it. The API derives the
    youtubeId from whatever link was pasted, so the reel never parses URLs. */
export type VideoReviewRow = {
  id: string
  name: string
  city: string
  videoUrl: string
  youtubeId: string
  createdAt: string
  updatedAt: string
}

export async function getVideoReviews(): Promise<VideoReviewRow[]> {
  const { data } = await http.get<{ rows: VideoReviewRow[] }>('/video-reviews')
  return data.rows
}

/* ---------- questions ---------- */

/** A question as the console filed it, in the order it put them.

    `answer` is markup — a paragraph, sometimes a list or a link. It was
    sanitised by the API at write time, which is what makes it renderable
    here; see api/src/utils/sanitize.ts for what a stored answer may contain. */
export type FaqRow = {
  id: string
  question: string
  answer: string
  answerText: string
  position: number
}

/** Only the published ones — the endpoint decides that, not this. */
export async function getFaqs(): Promise<FaqRow[]> {
  const { data } = await http.get<{ rows: FaqRow[] }>('/faqs')
  return data.rows
}

/* ---------- bookings ---------- */

export type BookingDraft = {
  variantId: string
  year: number
  fullName: string
  city: string
  address: string
  gender: string
  contact: string
  email: string
  /** A preference; the desk chooses the actual car later. */
  colour?: string
  /** The payment slip. Required — a booking without one is not a booking. */
  receipt: File
}

export type Booking = {
  id: string
  reference: string
  vehicleId: string
  vehicle: {
    id: string
    title: string
    brand: string
    model: string
    variant: string
    year: number
    price: number
    image: string
  }
  fullName: string
  city: string
  address: string
  gender: string
  contact: string
  email: string
  note?: string
  receipt: { url: string; name: string; mime: string; size: number }
  status: 'pending' | 'verified' | 'confirmed' | 'cancelled'
  createdAt: string
  updatedAt: string
}

/* Multipart, because a receipt is a file. The Content-Type header is left
   alone on purpose: the browser has to set it itself so the multipart
   boundary it generates is the one it actually writes. */
export async function createBooking(draft: BookingDraft): Promise<Booking> {
  const form = new FormData()

  form.set('variantId', draft.variantId)
  form.set('year', String(draft.year))
  form.set('fullName', draft.fullName)
  form.set('city', draft.city)
  form.set('address', draft.address)
  form.set('gender', draft.gender)
  form.set('contact', draft.contact)
  form.set('email', draft.email)
  if (draft.colour) form.set('colour', draft.colour)
  form.set('receipt', draft.receipt)

  const { data } = await http.post<Booking>('/bookings', form)
  return data
}

function isNotFound(error: unknown) {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    (error as { response?: { status?: number } }).response?.status === 404
  )
}
